import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import Region from '../Region';
import UIAssetKeys from '../UIAssetKeys';
import gameData, { AREAS, getFoundShapesInArea, getTotalFoundShapes, getTotalRequiredShapes, REGIONS } from '../gameData';
import GreenhouseArea from './GreenhouseArea';
import RoverArea from './RoverArea';
import DomeArea from './DomeArea';
import { GameEvents, GameStates } from '../scenes/Game';
import SoundAssetKeys from '../SoundAssetKeys';

export default class SpaceRegion extends Region
{
    /**
     * Create the Space region screen
     * @param {Phaser.Scene} scene the owner scene
     */
    constructor(scene)
    {
        super(scene, REGIONS.SPACE);

        this.isLocked = gameData.region_unlock_requirements[REGIONS.SPACE] > getTotalFoundShapes();

        this.background = ABCya.Images.addImage(0, 0, UIAssetKeys._11_Space_Spc_BG, this).setOrigin(0);

        this._addGraphics();
        this._createMenuButton(UIAssetKeys._Buttons_Space_Btn_Spc_Menu3, UIAssetKeys._Buttons_Space_Btn_Spc_Menu2, UIAssetKeys._Buttons_Space_Btn_Spc_Menu1);
        this._createProgressButton(UIAssetKeys._Buttons_Btn_Spc_Prgrs3, UIAssetKeys._Buttons_Btn_Spc_Prgrs2, UIAssetKeys._Buttons_Btn_Spc_Prgrs1);
        this._createInformation(UIAssetKeys._11_Space_HldngShp_Spc, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0xffffff, 0x2c2c89, 0x67adb9);
        this._createBackButton(UIAssetKeys._Buttons_Space_Btn_Spc_Menu3, UIAssetKeys._Buttons_Space_Btn_Spc_Back2, UIAssetKeys._Buttons_Space_Btn_Spc_Back1);

        this.greenhouseArea = new GreenhouseArea(this);
        this.roverArea = new RoverArea(this);
        this.domeArea = new DomeArea(this);

        this._createEventListeners();

        this.greenhouseProgress = this._createAreaProgressCounter(371, 234, UIAssetKeys._11_Space_HldngShp_Spc, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[this.region].GREENHOUSE);
        this.roverProgress = this._createAreaProgressCounter(388, 496, UIAssetKeys._11_Space_HldngShp_Spc, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[this.region].ROVER);
        this.domeProgress = this._createAreaProgressCounter(851, 478, UIAssetKeys._11_Space_HldngShp_Spc, UIAssetKeys._03_Beach_HldingShp_Cmplt, 0x2c2c89, AREAS[this.region].DOME);

        this._createGreatWorkPopup();
        this._createRegionUnlockedPopup();

        this.jingleAudio = ABCya.Audio.createSound(this.scene);

        //this._addReference("SpaceRegionZoomedOut", "assets/reference_images/11 Get In Shape_Space_1-01.jpg");
        //this._addReference("SpaceRegionZoomedOut-AreaCounters", "assets/reference_images/11 Get In Shape_Space_2-01.jpg");
        //this._addReference("SpaceRegionZoomedIn-Greenhouse", "assets/reference_images/11 Get In Shape_Space_3-01.jpg");
        //this._addReference("SpaceRegionZoomedIn-Rover", "assets/reference_images/11 Get In Shape_Space_5-01.jpg");
        //this._addReference("SpaceRegionZoomedIn-Dome", "assets/reference_images/11 Get In Shape_Space_4-01.jpg");
        //ABCya.Debug.enableImagePlacerDebug(this.scene, this.background, this.ground);

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.greenhouse.disableInteractive();
        this.rover.disableInteractive();
        this.dome.disableInteractive();

        this.greenhouseArea.reset();
        this.roverArea.reset();
        this.domeArea.reset();
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.greenhouse.disableInteractive();
        this.rover.disableInteractive();
        this.dome.disableInteractive();

        this.greenhouseProgress.setAlpha(0);
        this.domeProgress.setAlpha(0);
        this.roverProgress.setAlpha(0);

        this.updateRegionData();

        this.greenhouseArea.reset();
        this.roverArea.reset();
        this.domeArea.reset();
    }

    /**
     * Animates the states in.
     */
    async begin()
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._space_drop_in, this.scene, 1, false, 0.5);

        let tweens = [
            { targets: this.background, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 0 },
            { targets: this.ground, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.8], props: { y: this.ground.onScreenPosition.y }, offset: 0 },

            { targets: this.dome, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.dome.onScreenPosition.y }, offset: 300 },
            { targets: this.tunnel, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.tunnel.onScreenPosition.y }, offset: 400 },
            { targets: this.greenhouse, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.greenhouse.onScreenPosition.y }, offset: 500 },
            { targets: this.roverContainer, duration: 750, ease: Phaser.Math.Easing.Back.Out, easeParams: [.4], props: { y: this.roverContainer.onScreenPosition.y }, offset: 600 },
            { targets: this.stars0, duration: 750, ease: Phaser.Math.Easing.Bounce.In, props: { alpha: 1 }, offset: 800 },

            { targets: this.shadows, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: 1200 },
            { targets: this.informationContainer, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { x: this.informationContainer.onScreenPosition.x }, offset: 1200 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 1300 },
            { targets: this.progressButton, duration: 750, ease: Phaser.Math.Easing.Back.Out, props: { scale: 1 }, offset: 1400 },
        ];

        this._createAreaProgressFadeIn(tweens, 1500);

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.menuButton.setInteractive();
                this.progressButton.setInteractive();

                this.greenhouse.setInteractive();
                this.rover.setInteractive();
                this.dome.setInteractive();

                this._createIdleAnimations();

                ABCya.Audio.playSound(SoundAssetKeys._space_jingle, this.jingleAudio, 1, true);
            }
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Animates the state out.
     */
    async leave()
    {
        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();

        this.greenhouse.disableInteractive();
        this.rover.disableInteractive();
        this.dome.disableInteractive();

        this._stopIdleAnimations();

        ABCya.Audio.playOneShot(SoundAssetKeys._space_jump_up, this.scene, 1, false, 0.75);

        let tweens = [
            { targets: this.jingleAudio, duration: 750, ease: Phaser.Math.Easing.Quadratic.Out, props: { volume: 0 }, offset: 0, onComplete: () => this.jingleAudio.stop() },
            { targets: this.background, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 1500 },
            { targets: this.ground, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.8], props: { y: this.ground.offScreenPosition.y }, offset: 1500 },

            // place region specific things here

            { targets: this.dome, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.dome.offScreenPosition.y }, offset: 1200 },
            { targets: this.tunnel, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.tunnel.offScreenPosition.y }, offset: 1100 },
            { targets: this.greenhouse, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.greenhouse.offScreenPosition.y }, offset: 1000 },
            { targets: this.roverContainer, duration: 750, ease: Phaser.Math.Easing.Back.In, easeParams: [.4], props: { y: this.roverContainer.offScreenPosition.y }, offset: 900 },
            { targets: this.stars0, duration: 750, ease: Phaser.Math.Easing.Bounce.Out, props: { alpha: 0 }, offset: 700 },
            { targets: this.shadows, duration: 750, ease: Phaser.Math.Easing.Quadratic.In, props: { alpha: 0 }, offset: 300 },
            { targets: this.informationContainer, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { x: this.informationContainer.offScreenPosition.x }, offset: 300 },
            { targets: this.menuButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 200 },
            { targets: this.progressButton, duration: 750, ease: Phaser.Math.Easing.Back.In, props: { scale: 0 }, offset: 100 },
            {
                targets: [this.domeProgress, this.greenhouseProgress, this.roverProgress],
                duration: 500,
                ease: Phaser.Math.Easing.Quadratic.In,
                props: { alpha: 0 },
                offset: 0
            }
        ];

        let timeline = this.scene.tweens.timeline({
            tweens: tweens,
        });

        let waitForTimeline = new Promise((resolve) =>
        {
            timeline.on(Phaser.Tweens.Events.TIMELINE_COMPLETE, () => resolve());
        })
        await waitForTimeline;
    }

    /**
     * Send all game objects to their start positions. Some game objects aren't sent "offscreen" but instead set to scale of 0 or alpha of 0.
     */
    _sendTargetsOffScreen()
    {
        super._sendTargetsOffScreen();

        this._sendTargetsOffScreenAll(
            [this.ground, this.tunnel, this.greenhouse, this.dome, this.roverContainer]);

        this.shadows.forEach(shadow => shadow.setAlpha(0));
        this.background.setAlpha(0);
        this.stars0.setAlpha(0);
    }

    /**
     * Adds the graphics for the region.
     */
    _addGraphics()
    {
        this.add(this.starContainer = this.scene.add.container(566, 389));
        this.stars0 = ABCya.Images.addImage(0, 0, UIAssetKeys._11_Space_Stars_Sky, this.starContainer);
        this.stars0.mask = ABCya.Graphics.addEllipse(565, 272, 1150, 650, 0xff0000, this.scene).setAlpha(0).createGeometryMask();

        this.ground = ABCya.Images.addImage(567, 411, UIAssetKeys._11_Space_Spc_Grnd, this);

        this.tunnelShadow = ABCya.Images.addImage(597, 291, UIAssetKeys._11_Space_Tnl_Shdw, this);
        this.roverShadow = ABCya.Images.addImage(556, 530, UIAssetKeys._11_Space_Rover_Rvr_Shdw, this);
        this.domeShadow = ABCya.Images.addImage(852, 397, UIAssetKeys._11_Space_Dome_Shdw, this);

        this.tunnel = ABCya.Images.addImage(626, 267, UIAssetKeys._11_Space_Tnl, this);

        this.greenhouse = ABCya.Images.addImage(475, 296, UIAssetKeys._12_Space_Zoom_In_Greenhouse_Grnhous_1, this).setScale(0.5156).setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });

        this.dome = ABCya.Images.addImage(784, 344, UIAssetKeys._12_Space_Zoom_In_Dome_Dome_1, this).setScale(0.5697).setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });

        this.add(this.roverContainer = this.scene.add.container());
        this.backRoverTire = ABCya.Images.addImage(635, 531, UIAssetKeys._11_Space_Rover_Rvr_BkTire, this.roverContainer).setScale(0.44);
        this.rover = ABCya.Images.addImage(538, 504, UIAssetKeys._11_Space_Rover_Rvr_Bdy, this.roverContainer).setScale(0.44);
        this.roverTire0 = ABCya.Images.addImage(446, 526, UIAssetKeys._11_Space_Rover_Rvr_FrntTire, this.roverContainer).setScale(0.44);
        this.roverTire1 = ABCya.Images.addImage(495, 555, UIAssetKeys._11_Space_Rover_Rvr_FrntTire, this.roverContainer).setScale(0.44);
        this.roverTire2 = ABCya.Images.addImage(544, 583, UIAssetKeys._11_Space_Rover_Rvr_FrntTire, this.roverContainer).setScale(0.44);

        this.rover.setInteractive({ useHandCursor: true, pixelPerfect: true, alphaTolerance: 0.5 });

        this._setAnimationPositionsAll([
            this.ground, this.tunnel, this.greenhouse, this.dome, this.roverContainer
        ]);

        this.shadows = [this.tunnelShadow, this.roverShadow, this.domeShadow];
    }

    /**
     * Create the event listeners for the objects that allow the user to zoom into the individual areas.
     */
    _createEventListeners()
    {
        this.greenhouse.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(SpaceRegionStates.GREENHOUSE));
        this.dome.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(SpaceRegionStates.DOME));
        this.rover.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this._zoomIntoArea(SpaceRegionStates.ROVER));
    }

    /**
     * Creates the idle animations. If the idle animations are already playing, it stops it, resets everything, and starts it over.
     */
    _createIdleAnimations()
    {
        this._stopIdleAnimations();

        this._createStarAnimation();

        let tweens = [
            { targets: this.roverContainer, duration: 2000, ease: Phaser.Math.Easing.Quadratic.InOut, props: { x: "-=40", y: "-=22" }, offset: 0, hold: 2000, repeatDelay: 2000 },
            { targets: this.roverShadow, duration: 2000, ease: Phaser.Math.Easing.Quadratic.InOut, props: { x: "-=40", y: "-=22" }, offset: 0, hold: 2000, repeatDelay: 2000 },
            { targets: this.roverTire0, duration: 500, ease: Phaser.Math.Easing.Quadratic.In, props: { y: "-=4" }, offset: 600, repeatDelay: 3000 },
            { targets: this.roverTire2, duration: 500, ease: Phaser.Math.Easing.Quadratic.In, props: { y: "-=4" }, offset: 750, repeatDelay: 3000 },
            { targets: this.roverTire1, duration: 500, ease: Phaser.Math.Easing.Quadratic.In, props: { y: "-=4" }, offset: 900, repeatDelay: 3000 },
            { targets: this.backRoverTire, duration: 500, ease: Phaser.Math.Easing.Quadratic.In, props: { y: "-=4" }, offset: 800, repeatDelay: 3000 }
        ];

        this.idleAnimations = this.scene.tweens.timeline({
            tweens: tweens,
            repeat: -1,
            yoyo: true,
        });
    }

    /**
     * Creates the star animation which doesn't get reset when idle animations are stopped.
     */
    _createStarAnimation()
    {
        let tweens = [
            { targets: this.starContainer, duration: 2000, ease: Phaser.Math.Easing.Linear, props: { alpha: 0.7 }, offset: 0 },
            { targets: this.starContainer, duration: 600000, ease: Phaser.Math.Easing.Linear, props: { angle: "+=360" }, yoyo: false, offset: 0 },
        ];

        this.starAnimation = this.scene.tweens.timeline({
            tweens: tweens,
            repeat: -1,
            yoyo: true,
        });
    }

    /**
     * Stops the idle animations and resets everything back to their default spots.
     */
    _stopIdleAnimations()
    {
        if (this.idleAnimations != null)
        {
            this.idleAnimations.data.forEach((tween) => 
            {
                if (tween.hasStarted)
                {
                    tween.data.forEach((data) =>
                    {
                        data.target[data.key] = data.start;
                    });
                }
            });
            this.idleAnimations.stop();
        }

        if (this.starAnimation != null && this.starAnimation.isPlaying())
        {
            this.starAnimation.stop();
        }

        this.starContainer.alpha = 1;
    }

    /**
     * Call this function to update all the game data associated with this region;
     * This can be used to make the game in sync with the actual game data (after a new game reset for example).
     * Should also be called before entering the region state.
     */
    updateRegionData()
    {
        super.updateRegionData();

        this._updateAreaProgress(this.greenhouseProgress, getFoundShapesInArea(this.region, AREAS[this.region].GREENHOUSE));

        this._updateAreaProgress(this.roverProgress, getFoundShapesInArea(this.region, AREAS[this.region].ROVER));

        this._updateAreaProgress(this.domeProgress, getFoundShapesInArea(this.region, AREAS[this.region].DOME));
    }

    /**
     * Zooms into the desired area.
     * @param {string} area the AREAS enum value
     */
    _zoomIntoArea(area)
    {
        super._zoomIntoArea(area);

        this._currentState = area;
        this._stopIdleAnimations();

        this.menuButton.disableInteractive();
        this.progressButton.disableInteractive();

        this.greenhouse.disableInteractive();
        this.rover.disableInteractive();
        this.dome.disableInteractive();

        let tweens = [];

        if (this._currentState == SpaceRegionStates.GREENHOUSE)
        {
            tweens.push(...this.greenhouseArea.zoomIn());
        }
        else if (this._currentState == SpaceRegionStates.ROVER)
        {
            tweens.push(...this.roverArea.zoomIn());
        }
        else if (this._currentState == SpaceRegionStates.DOME)
        {
            tweens.push(...this.domeArea.zoomIn());
        }

        tweens.push(
            {
                targets: [
                    this.ground, this.domeProgress, this.greenhouseProgress, this.roverProgress, this.tunnel, this.stars0,
                    ...this.shadows
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
                offset: 0
            },
            { targets: [this.menuButton, this.progressButton], duration: 500, props: { scale: 0 }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.informationContainer, duration: 500, props: { x: this.informationContainer.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.backButton, duration: 500, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
        );

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.backButton.setInteractive();
                if (this._currentState == SpaceRegionStates.GREENHOUSE)
                {
                    this.greenhouseArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == SpaceRegionStates.ROVER)
                {
                    this.roverArea.setAreaShapeButtonInteractable(true);
                }
                else if (this._currentState == SpaceRegionStates.DOME)
                {
                    this.domeArea.setAreaShapeButtonInteractable(true);
                }
            }
        });
    }

    /**
     * Leaves the zoomed in area.
     * @param {Boolean} showCompletionScreen if true, the completion screen will be shown after the zoom out animation is complete; false otherwise
     */
    _leaveZoomedInState(showCompletionScreen = false)
    {
        super._leaveZoomedInState();

        this.updateRegionData();

        this.backButton.disableInteractive();

        let tweens = [];

        if (this._currentState == SpaceRegionStates.GREENHOUSE)
        {
            tweens.push(...this.greenhouseArea.zoomOut());
        }
        else if (this._currentState == SpaceRegionStates.ROVER)
        {
            tweens.push(...this.roverArea.zoomOut());
        }
        else if (this._currentState == SpaceRegionStates.DOME)
        {
            tweens.push(...this.domeArea.zoomOut());
        }

        tweens.push(
            {
                targets: [
                    this.ground, this.tunnel, this.stars0
                ],
                duration: 500,
                props: { alpha: 1 },
                ease: Phaser.Math.Easing.Quadratic.Out,
                offset: 500
            },
            { targets: this.backButton, duration: 500, props: { scale: 0 }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: [this.menuButton, this.progressButton], duration: 500, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 750 },
            { targets: this.informationContainer, duration: 500, props: { x: this.informationContainer.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
            { targets: this.shadows, duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 1000 },
        );

        this._createAreaProgressFadeIn(tweens, 1000);

        this.scene.tweens.timeline({
            tweens: tweens,
            onComplete: () =>
            {
                this.greenhouseArea.reset();
                this.roverArea.reset();
                this.domeArea.reset();

                let totalFindableShapes = getTotalRequiredShapes(REGIONS.BEACH) + getTotalRequiredShapes(REGIONS.DOWNTOWN) + getTotalRequiredShapes(REGIONS.CAMP) + getTotalRequiredShapes(REGIONS.SPACE);
                if (showCompletionScreen && getTotalFoundShapes() == totalFindableShapes)
                {
                    // we've found all the shapes, go to the completion screen
                    this.scene.events.emit(GameEvents.GO_TO_STATE, GameStates.COMPLETION);
                }
                else
                {
                    this.menuButton.setInteractive();
                    this.progressButton.setInteractive();

                    this.greenhouse.setInteractive();
                    this.rover.setInteractive();
                    this.dome.setInteractive();

                    this._createIdleAnimations();
                }
            }
        });
    }

    /**
     * Create area progress fade in tweens for areas that have been found.
     * @param {any[]} tweens the current array of tweens
     * @param {number} offset the offset of the progress fade in tweens
     */
    _createAreaProgressFadeIn(tweens, offset)
    {
        if (gameData.saved_data[this.region][AREAS[this.region].GREENHOUSE].found)
        {
            tweens.push({ targets: this.greenhouseProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[this.region].DOME].found)
        {
            tweens.push({ targets: this.domeProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
        if (gameData.saved_data[this.region][AREAS[this.region].ROVER].found)
        {
            tweens.push({ targets: this.roverProgress, duration: 500, ease: Phaser.Math.Easing.Quadratic.Out, props: { alpha: 1 }, offset: offset });
        }
    }
}

/**
 * The individual area states for the Space region.
 */
export const SpaceRegionStates = {
    GREENHOUSE: "Greenhouse",
    DOME: "Dome",
    ROVER: "Rover",
}